import { useEffect, useRef, useState } from 'react'
import type { Suggestion } from '../api'
import { api, ApiError } from '../api'
import type { Group } from '../tree'
import { FIELD_LABELS, cond, withIds } from '../tree'

export const EXAMPLES: [string, string][] = [
  ['Beatles im Star-Club', 'ort:Star-Club (person:"John Lennon" OR person:"Paul McCartney")'],
  ['Gorleben 1977–1981', 'ort:Gorleben jahr:1977-1981'],
  ['Domenica mit Willi Bartels', 'person:"Domenica Niehoff" person:"Willi Bartels"'],
  ['Grenzöffnung DDR', 'sammlung:"Grenzöffnung DDR"'],
  ['Hafenstraße, nur Schwarzweiß', 'ort:Hafenstraße (technik:"Schwarzweißfotografie (Negativ)" OR technik:"Schwarzweißfotografie (Abzug)")'],
]

type Props = {
  onAdd: (nodes: Group['items']) => void
  onReplace: (tree: Group) => void
  onError: (msg: string | null) => void
}

export function SearchBar({ onAdd, onReplace, onError }: Props) {
  const [q, setQ] = useState('')
  const [items, setItems] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const [sel, setSel] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const wrap = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement
      if (e.key === '/' && !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) {
        e.preventDefault()
        input.current?.focus()
      }
    }
    const onClick = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false) }
    window.addEventListener('keydown', onKey)
    window.addEventListener('click', onClick)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('click', onClick) }
  }, [])

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2 || /[:()"]/.test(term)) { setItems([]); return }
    const ctrl = new AbortController()
    const t = setTimeout(() => {
      api.suggest(term, ctrl.signal).then((r) => { setItems(r); setSel(0); setOpen(true) }).catch(() => undefined)
    }, 150)
    return () => { clearTimeout(t); ctrl.abort() }
  }, [q])

  const options: Suggestion[] = q.trim().length >= 2 ? [{ field: 'text', id: null, label: q.trim(), path: ['in allen Feldern'], count: null }, ...items] : []

  async function submitText(text: string) {
    if (!text.trim()) return
    try {
      const r = await api.parse(text)
      onAdd(withIds(r.query).items.length ? withIds(r.query).items : [])
      setQ('')
      setOpen(false)
      onError(null)
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Die Eingabe konnte nicht gelesen werden.')
    }
  }

  function take(s: Suggestion) {
    if (s.field === 'text') return submitText(s.label)
    if (s.field === 'inv') onAdd([cond('inv', s.label)])
    else onAdd([cond(s.field, s.id, s.label)])
    setQ('')
    setOpen(false)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || !options.length) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setSel((s) => (s + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length)
    } else if (e.key === 'Escape') setOpen(false)
  }

  let lastField = ''
  return (
    <>
      <div className="qwrap" ref={wrap}>
        <form className="qbox" autoComplete="off" onSubmit={(e) => { e.preventDefault(); if (open && sel > 0 && options[sel]) take(options[sel]); else submitText(q) }}>
          <input id="q" ref={input} type="search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            onFocus={() => items.length && setOpen(true)}
            placeholder="Suchen: Personen, Orte, Ereignisse, Inventarnummern …" aria-label="Suche" aria-autocomplete="list" aria-controls="suggest" />
          <button className="go" type="submit">Suchen</button>
        </form>
        {open && options.length > 0 && (
          <div className="suggest" id="suggest" role="listbox">
            {options.map((s, i) => {
              const head = s.field !== 'text' && s.field !== lastField ? <div className="grp label">{FIELD_LABELS[s.field] ?? s.field}</div> : null
              lastField = s.field
              return (
                <div key={`${s.field}-${s.id ?? s.label}-${i}`}>
                  {head}
                  <button type="button" role="option" aria-selected={i === sel} onMouseEnter={() => setSel(i)} onClick={() => take(s)}>
                    <span>{s.field === 'text' ? <>Freitext »{s.label}«</> : <Hl text={s.label} q={q} />}</span>
                    <span className="path">{s.path.join(' › ')}{s.count != null && ` · ${s.count.toLocaleString('de-DE')}`}</span>
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
      <div className="hint">
        <span><code>person:"John Lennon"</code></span>
        <span><code>ort:Gorleben</code></span>
        <span><code>jahr:1977-1981</code></span>
        <span><code>-technik:Farbdia</code> schließt aus</span>
        <span><code>( … OR … )</code> für Alternativen</span>
        <span><code>/</code> fokussiert die Suche</span>
      </div>
      <div className="examples">
        <span className="label">Beispiele</span>
        {EXAMPLES.map(([label, text]) => (
          <button key={label} onClick={async () => {
            try { onReplace(withIds((await api.parse(text)).query)); onError(null) } catch (e) { onError((e as Error).message) }
          }}>{label}</button>
        ))}
      </div>
    </>
  )
}

function Hl({ text, q }: { text: string; q: string }) {
  const i = text.toLowerCase().indexOf(q.trim().toLowerCase())
  if (i < 0 || !q.trim()) return <>{text}</>
  const n = q.trim().length
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + n)}</mark>{text.slice(i + n)}</>
}
